import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { adminGetEnabledClients, getProfile, AdminClient } from '../lib/api';

/**
 * Global "acting client" selection, rendered as a dropdown in the header.
 *
 * - Admins pick among all enabled clients (must pick one; persisted).
 * - Multi-store users (>1 assigned Logiwa client on their profile) pick among
 *   THEIR stores; defaults to their primary store, so nothing blocks.
 * - Single-store users have no selection and send no clientId (unchanged).
 *
 * The choice persists per user in localStorage. Module availability for the
 * selected client is enforced per-route by <ClientGate>.
 */
interface ClientSelectionContextType {
  selectedClientId: string;
  setSelectedClientId: (id: string) => void;
  /** The effective clientId to pass to API calls (undefined for single-store users) */
  effectiveClientId: string | undefined;
  clients: Array<{ id: string; name: string }>;
  /** Full record (incl. module flags) of the selected client — admins only */
  selectedClient: AdminClient | undefined;
  /** Enabled modules of the selected store — multi-store users only */
  selectedStoreModules: string[] | undefined;
  /** Whether the header dropdown should be rendered */
  showSelector: boolean;
  isAdmin: boolean;
}

const ClientSelectionContext = createContext<ClientSelectionContextType>({
  selectedClientId: '',
  setSelectedClientId: () => {},
  effectiveClientId: undefined,
  clients: [],
  selectedClient: undefined,
  selectedStoreModules: undefined,
  showSelector: false,
  isAdmin: false,
});

const storageKeyFor = (userId: string) => `arivo:selectedClient:${userId}`;

export function ClientSelectionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin === true;
  const userId = user?.id ?? '';

  const [selectedClientId, setSelectedClientIdState] = useState<string>(() => {
    if (!userId) return '';
    try {
      return localStorage.getItem(storageKeyFor(userId)) || '';
    } catch {
      return '';
    }
  });

  const setSelectedClientId = (id: string) => {
    setSelectedClientIdState(id);
    if (userId) {
      try {
        localStorage.setItem(storageKeyFor(userId), id);
      } catch {
        // localStorage unavailable (private mode) — selection just won't persist
      }
    }
  };

  // Auth is restored asynchronously, so userId is empty during the initial
  // state initializer on a full page load. Rehydrate again once the actual
  // user is known (and clear the previous user's selection on sign-out).
  useEffect(() => {
    if (!userId) {
      setSelectedClientIdState('');
      return;
    }

    try {
      setSelectedClientIdState(localStorage.getItem(storageKeyFor(userId)) || '');
    } catch {
      setSelectedClientIdState('');
    }
  }, [userId]);

  const { data: clientsData } = useQuery({
    queryKey: ['clientSelection', 'enabledClients'],
    queryFn: adminGetEnabledClients,
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000,
  });

  // Shares the ['profile'] cache with Layout/useEnabledModules — no extra request.
  const { data: profileData } = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    enabled: !isAdmin,
    staleTime: 5 * 60 * 1000,
  });
  const assignedClients = profileData?.assignedClients;
  const isMultiStore = !isAdmin && (assignedClients?.length ?? 0) > 1;

  // Drop a stored selection that's no longer valid: for admins once the
  // client list loads (client removed/disabled), for multi-store users once
  // the profile loads (store unassigned). Falls back to '' — which for
  // multi-store users means the primary store.
  useEffect(() => {
    if (!selectedClientId) return;
    const valid = isAdmin
      ? !clientsData || clientsData.clients.some((c) => c.id === selectedClientId)
      : !assignedClients || assignedClients.some((c) => c.id === selectedClientId);
    if (!valid) {
      setSelectedClientIdState('');
      if (userId) {
        try {
          localStorage.removeItem(storageKeyFor(userId));
        } catch {
          // ignore
        }
      }
    }
  }, [isAdmin, clientsData, assignedClients, selectedClientId, userId]);

  const value = useMemo<ClientSelectionContextType>(() => {
    const adminList = clientsData?.clients ?? [];
    const clients = isAdmin
      ? adminList
          .map((c) => ({ id: c.id, name: c.name }))
          .sort((a, b) => a.name.localeCompare(b.name))
      : (assignedClients ?? []).map((c) => ({ id: c.id, name: c.name }));

    let effectiveClientId: string | undefined;
    if (isAdmin) {
      effectiveClientId = selectedClientId || undefined;
    } else if (isMultiStore && assignedClients?.[0]) {
      effectiveClientId = selectedClientId || assignedClients[0].id;
    }

    return {
      selectedClientId,
      setSelectedClientId,
      effectiveClientId,
      clients,
      selectedClient:
        isAdmin && effectiveClientId ? adminList.find((c) => c.id === effectiveClientId) : undefined,
      selectedStoreModules:
        isMultiStore && effectiveClientId
          ? assignedClients?.find((c) => c.id === effectiveClientId)?.enabledModules
          : undefined,
      showSelector: isAdmin || isMultiStore,
      isAdmin,
    };
    // setSelectedClientId is stable in behavior (depends only on userId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientsData, assignedClients, isMultiStore, selectedClientId, isAdmin, userId]);

  return <ClientSelectionContext.Provider value={value}>{children}</ClientSelectionContext.Provider>;
}

export function useClientSelection() {
  return useContext(ClientSelectionContext);
}
